import type { Span } from "../../bridge/binary";
import type { AxConnectorType, AxFill, AxFontFamilyVariant, AxLineStyle, AxLinkToObjectInfo, AxObjectType, AxScrollbars, AxWidgetStyle, DropType, EasingType, Selection, SliceType, StrokeAlignment } from "../../bridge/selection";
import type { BufferInfo } from "../../common/persistence";
import type { AxStringAlignment, TextListType } from "../../platform/attributedString";
import type { MenuItem, Rect } from "../models";
import type { ShortcutType } from "@/generated/models";
import { useBinaryReader, useJsonReader } from "../../bridge/binary";
import { createProxies } from "./proxies";

export interface AxPage {
  pageId: string;
  pageName: string;
}

export interface DiagramObjectData {
  id: string;
  name: string;
  text?: string;
  type: AxObjectType;
  rect: Rect;
  visible: boolean;
  style: AxWidgetStyle;
  svg: string;
  selected: boolean;
  children: DiagramObjectData[];
  htmlText: string;
  imageUrl: string;
  scrollbars?: AxScrollbars;
  clipContent?: boolean;
  linkToObjectInfo?: AxLinkToObjectInfo;
}

export enum FileEnvironmentContext {
  Regular = 0,
  Library = 1,
  Template = 2,
  Private = 3,
}

export interface GeometryValue { value: number; isScaling?: boolean }

export interface ColorValue { value: number }

/// keep-sorted
export interface StyleProps {
  "auto-fit-height": boolean;
  "auto-fit-width": boolean;
  "backdrop-blur-enabled": boolean;
  "backdrop-blur-radius": number;
  "border-fill": number;
  "border-visibility": number;
  "border-width": number;
  "character-spacing": number;
  "corner-radius-bottom-left": number;
  "corner-radius-bottom-right": number;
  "corner-radius-top-left": number;
  "corner-radius-top-right": number;
  "corner-radius": number;
  "end-cap": number;
  "fit-to-image": boolean;
  "fixed-height": number;
  "fixed-width": number;
  "font-color": number;
  "font-family": string;
  "font-size": number;
  "font-stretch": number;
  "font-style": number;
  "font-weight": number;
  "horizontal-align": AxStringAlignment;
  "inner-shadow-blur": number;
  "inner-shadow-enabled": boolean;
  "inner-shadow-fill": number;
  "inner-shadow-spread": number;
  "inner-shadow-x": number;
  "inner-shadow-y": number;
  "letter-casing": number;
  "line-end": number;
  "line-join": number;
  "line-pattern": AxLineStyle;
  "line-spacing": number;
  "line-start": number;
  "list-info": boolean;
  "loc-x": GeometryValue;
  "loc-y": GeometryValue;
  "miter-angle": number;
  "outer-shadow-blur": number;
  "outer-shadow-fill": number;
  "outer-shadow-spread": number;
  "outer-shadow-x": number;
  "outer-shadow-y": number;
  "outer-shadow": number;
  "padding-bottom": number;
  "padding-horizontal": number;
  "padding-left": number;
  "padding-right": number;
  "padding-top": number;
  "padding-vertical": number;
  "shadow-enabled": boolean;
  "start-cap": number;
  "stroke-alignment": StrokeAlignment;
  "vertical-align": AxStringAlignment;
  "widget-blur-enabled": boolean;
  "widget-blur-radius": number;
  baseline: number;
  fill: number;
  height: GeometryValue;
  italic: boolean;
  opacity: number;
  rotation: number;
  strikethrough: boolean;
  underline: boolean;
  visibility: boolean;
  width: GeometryValue;
}

/// keep-sorted
interface StyleRefProps {
  "border-fill": ColorValue;
  "font-color": ColorValue;
  "outer-shadow-fill": ColorValue;
  fill: ColorValue;
}

export type StylePropName = keyof StyleProps;
export type StylePropRefName = keyof StyleRefProps;

export type StylePropAutoFit = Extract<StylePropName, "auto-fit-width" | "auto-fit-height">;
export type StylePropFill = Extract<StylePropName, "fill" | "border-fill">;
export type StylePropColor = Extract<StylePropName, "fill" | "font-color" | "border-fill" | "outer-shadow-fill">;

export interface StyleUpdateBase {
  types?: AxObjectType;

  /**
   * Controls focus behavior when applying update. If true, the editor will be focused after applying the update. This is useful for updates that are triggered from the editor itself, to prevent focus loss. For updates triggered from outside of the editor, this should typically be false to avoid unexpected focus changes.
   *
   * defaults to `"text-editor"`
   */
  focus?: "none" | "text-editor";
}

export interface StyleUpdateOf<K extends StylePropName> extends StyleUpdateBase {
  key: K;
  value: StyleProps[K];
}

export interface StyleRefUpdateOf<K extends StylePropName> extends StyleUpdateBase {
  key: K;
  referenceId: string;
  referenceName: string;
  value: K extends StylePropRefName ? StyleRefProps[K] : StyleProps[K];
}

export type StyleUpdate = {
  [K in StylePropName]: StyleUpdateOf<K>;
}[StylePropName];

type RefSupportedProps = Extract<StylePropName, "font-size"
  | "font-family"
  | "font-color"
  | "fill"
  | "border-fill"
  | "outer-shadow-fill"
  | "outer-shadow"
  | "corner-radius"
  | "corner-radius-top-left"
  | "corner-radius-top-right"
  | "corner-radius-bottom-left"
  | "corner-radius-bottom-right"
  | "character-spacing"
  | "padding-left"
  | "padding-top"
  | "padding-right"
  | "padding-bottom">;

export type StyleRefUpdate = {
  [K in RefSupportedProps]: StyleRefUpdateOf<K>;
}[RefSupportedProps];

interface BridgeExportsMethods {
  // SetSelectionStyle: (prop: string, val: boolean | number | string) => boolean;
  ClearPreviewDiagramSelectionObject: () => void;
  CueCrop: (copy?: boolean, cut?: boolean) => void;
  ExitSliceImgMode: () => void;
  GetActiveGlobalCommandName: () => string | undefined;
  GetActiveEditorModeName: () => string | undefined;
  GetTrackedDiagramObject: (attachmentId: string) => string | undefined;
  SetTrackedDiagramObject: (attachmentId: string, objectId: string) => Promise<void>;
  RemoveTrackedDiagramObject: (attachmentId: string) => Promise<void>;
  GetAvailableFonts: () => Span<Record<string, AxFontFamilyVariant[]>>;
  GetCurrentPage: () => Span<AxPage>;
  GetCurrentSelection: () => Span<Selection>;
  GetDiagramObject: (objectId: string) => Span<DiagramObjectData | null>;
  GetDiagramObject2: (objectId: string) => Span<DiagramObjectData | null>;
  GetDiagramObjectAt: (x: number, y: number) => Span<DiagramObjectData | null>;
  GetDiagramObjects: () => Span<DiagramObjectData[] | null>;
  GetDiagramObjects2: () => Span<DiagramObjectData[] | null>;
  GetDiagramSections: () => Span<DiagramObjectData[]>;
  GetLimitedSelectionContextMenuItem: (isMultiselect: boolean) => MenuItem;
  GetProjectFonts: () => Span<string[]>;
  GetSelectionContextMenuItem: () => MenuItem;
  GetLibraryBehaviorContextMenuItem: () => MenuItem;
  ShowSelectionContextMenu: (objectId: string, x: number, y: number) => void;
  GetSelectionImgThumbnail: (objectId: string) => string | undefined;
  IsPointIntoView: (x: number, y: number) => boolean;
  IsDiagramEmpty: () => Promise<boolean>;
  PreviewDiagramSelectionObject: (objectId: string) => void;
  ScrollToObject: (objectId: string) => Promise<void>;
  ScrollToPoint: (x: number, y: number) => Promise<void>;
  SetFitToContent: (prop: string, val: boolean) => void;
  SetFitToImage: (val: boolean) => void;
  SetImage: (id: string, isClipboard: boolean) => boolean;
  SetSelectionConnectorFlowType: (type: AxConnectorType) => boolean;
  SetSelectionShape: (val: number) => void;
  SetSelectionStyleBool: (prop: StylePropName, val: boolean, targetTypes: AxObjectType) => boolean;
  SetSelectionStyleNum: (prop: StylePropName, val: number, targetTypes: AxObjectType) => boolean;
  SetSelectionStylesNum: (props: StylePropName[], val: number, targetTypes: AxObjectType) => boolean;
  SetSelectionStyleRef: (prop: StylePropName, referenceId: string, referenceName: string, value: number, targetTypes: AxObjectType) => boolean;
  SetSelectionStylesRef: (props: StylePropName[], referenceId: string, referenceName: string, value: number, targetTypes: AxObjectType) => boolean;
  SetSelectionStyleStr: (prop: StylePropName, val: string, targetTypes: AxObjectType) => boolean;
  SetSelectionStyleRefStr: (props: StylePropName, referenceId: string, referenceName: string, value: string, targetTypes: AxObjectType) => boolean;
  SetSelectionStyleRefColor: (prop: StylePropName, referenceId: string, referenceName: string, value: number, targetTypes: AxObjectType) => boolean;
  SetSelectionExportList: (exportListInfo: BufferInfo) => void;
  SetSectionColorStyle: (bgColor: number) => boolean;
  SetSelectionGeometryNum: (prop: string, val: number, targetTypes: AxObjectType, isScaling: boolean) => boolean;
  SetOutlineWidth: (width: number) => void;
  SetSidebarSize: (side: "left" | "right", width: number, height: number, state: "split" | "minimized" | "maximized") => void;
  SetFollowing: (connectionId: string) => void;
  SetSliceOrientation: (val: SliceType) => void;
  RenameSection: (objId: string) => void;
  SetupState: () => Promise<unknown>;
  SetIsDraggingCommentPin: (isDragging: boolean) => void;
  ClearWorkspaceEditors: () => void;
  ToggleSelectionListInfo: (listType: TextListType) => boolean;
  InsertSvg: (url: string, convertToShapes: boolean, x?: number, y?: number) => Promise<void>;
  InsertFrame: (name: string, width?: number, height?: number) => Promise<void>;
  OpenOuterObject: (packageId: string) => unknown;
  OpenDocument: (shortcutType: ShortcutType, documentId: string, objectId?: string) => Promise<void>;
  CloseDocument: () => Promise<void>;
  SetFileEnvironmentContext: (environmentContext: FileEnvironmentContext, isGuest: boolean) => void;
  OuterObjectPackageId: () => string;
  InsertLibraryWidget: (url: string, x?: number, y?: number) => void;
  InsertComponent: () => void;
  InsertAxvgMini: (axvg: string, locationX: number, locationY: number) => Promise<void>;
  GetInsertLocation: (screenWidth: number, screenHeight: number, screenCount: number) => string;
  InsertTemplate: (template: string) => Promise<void>;
  AddFill: (key: string, targetTypes: AxObjectType) => boolean;
  SetSelectionFill: (key: string, idx: number, value: number, targetTypes: AxObjectType) => boolean;
  SetSelectionFillItemVisibility: (key: string, idx: number, value: boolean, targetTypes: AxObjectType) => boolean;
  RemoveFill: (key: string, idx: number, targetTypes: AxObjectType) => boolean;
  SetSelectionFillItemType: (key: string, fillType: string, idx: number, targetTypes: AxObjectType) => boolean;
  SetGradientEditMode: (mode: boolean, key: string, index: number) => void;
  RotateGradient: (key: string, index: number) => boolean;
  FlipGradient: (key: string, index: number) => boolean;
  AddStop: (key: string, index: number, offset: number, color: number) => string;
  RemoveStop: (key: string, fillIndex: number, stopId: string) => boolean;
  UpdateGradientStop: (key: string, fillIndex: number, stopId: string, offset: number, color: number) => boolean;
  HighlightDiagramObjects: (objIds: string[]) => void;
  SelectDiagramObjects: (objIds: string[]) => void;
  RenameDiagramObject: (objId: string, newName: string) => void;
  ToggleShowDiagramObjects: () => void;
  ToggleLockDiagramObjects: () => void;
  SetPointerType: (pointerType: string) => void;
  SetLinkToObject: (objId: string, targetId?: string, easingType?: EasingType) => void;
  DragObjectsToTarget: (dragIds: string[], dropTargetId: string | undefined, dropType: DropType) => void;
  GetRecentFills: (includeGradients: boolean) => Span<AxFill[]>;
  SetFill: (key: string, fillIndex: number, bufferInfo: BufferInfo) => void;
  AddRecentFill: (bufferInfo: BufferInfo) => void;
  KillCombine: () => void;
  FocusCanvasOrEditor: () => void;
}

export interface BridgeExports {
  Bridge: BridgeExportsMethods;
}

const {
  setup,
  setupState,
  // setSelectionStyle,
  clearPreviewDiagramSelectionObject: clearPreviewDiagramSelectionObjectProxy,
  cueCrop,
  exitSliceImgMode,
  getActiveGlobalCommandName,
  getActiveEditorModeName,
  getTrackedDiagramObject,
  setTrackedDiagramObject,
  removeTrackedDiagramObject,
  getAvailableFonts: getAvailableFontsProxy,
  getCurrentPage: getCurrentPageProxy,
  getCurrentSelection,
  getDiagramObject: getDiagramObjectProxy,
  getDiagramObject2: getDiagramObject2Proxy,
  getDiagramObjectAt: getDiagramObjectAtProxy,
  getDiagramObjects: getDiagramObjectsProxy,
  getDiagramObjects2: getDiagramObjects2Proxy,
  getDiagramSections: getDiagramSectionsProxy,
  getLimitedSelectionContextMenuItem: getLimitedSelectionContextMenuItemProxy,
  getProjectFonts: getProjectFontsProxy,
  getSelectionContextMenuItem: getSelectionContextMenuItemProxy,
  getLibraryBehaviorContextMenuItem: getLibraryBehaviorContextMenuItemProxy,
  showSelectionContextMenu,
  getSelectionImgThumbnail,
  isPointIntoView,
  isDiagramEmpty,
  previewDiagramSelectionObject,
  scrollToObject,
  scrollToPoint,
  setFitToContent,
  setFitToImage,
  setImage,
  setSelectionConnectorFlowType,
  setSelectionShape,
  setSelectionStyleBool,
  setSelectionStyleNum,
  setSelectionStylesNum,
  setSelectionStyleRef,
  setSelectionStylesRef,
  setSelectionStyleRefStr,
  setSelectionStyleRefColor,
  setSelectionStyleStr,
  setSelectionExportList,
  setSectionColorStyle,
  setSelectionGeometryNum,
  setOutlineWidth,
  setSidebarSize,
  setFollowing,
  setPointerType,
  setSliceOrientation,
  renameSection,
  setIsDraggingCommentPin,
  clearWorkspaceEditors,
  toggleSelectionListInfo,
  insertSvg,
  insertFrame,
  openOuterObject,
  openDocument,
  closeDocument,
  setFileEnvironmentContext,
  outerObjectPackageId,
  insertLibraryWidget,
  insertComponent,
  insertTemplate,
  insertAxvgMini,
  getInsertLocation,
  addFill,
  setSelectionFill,
  setSelectionFillItemVisibility,
  removeFill,
  setSelectionFillItemType,
  setGradientEditMode,
  rotateGradient,
  addStop,
  updateGradientStop,
  highlightDiagramObjects,
  selectDiagramObjects,
  renameDiagramObject,
  toggleShowDiagramObjects,
  toggleLockDiagramObjects,
  setLinkToObject,
  dragObjectsToTarget,
  removeStop,
  flipGradient,
  getRecentFills: getRecentFillsProxy,
  setFill,
  addRecentFill,
  killCombine,
  focusCanvasOrEditor,
} = createProxies<BridgeExportsMethods>([
  "ClearPreviewDiagramSelectionObject",
  "CueCrop",
  "ExitSliceImgMode",
  "GetActiveGlobalCommandName",
  "GetActiveEditorModeName",
  "GetTrackedDiagramObject",
  "SetTrackedDiagramObject",
  "RemoveTrackedDiagramObject",
  "GetAvailableFonts",
  "GetCurrentPage",
  "GetCurrentSelection",
  "GetDiagramObject",
  "GetDiagramObject2",
  "GetDiagramObjectAt",
  "GetDiagramObjects",
  "GetDiagramObjects2",
  "GetDiagramSections",
  "GetLimitedSelectionContextMenuItem",
  "GetProjectFonts",
  "GetSelectionContextMenuItem",
  "GetLibraryBehaviorContextMenuItem",
  "ShowSelectionContextMenu",
  "GetSelectionImgThumbnail",
  "IsPointIntoView",
  "IsDiagramEmpty",
  "PreviewDiagramSelectionObject",
  "ScrollToObject",
  "ScrollToPoint",
  "SetFitToContent",
  "SetFitToImage",
  "SetImage",
  "SetSelectionConnectorFlowType",
  "SetSelectionShape",
  "SetSelectionStyleBool",
  "SetSelectionStyleNum",
  "SetSelectionStylesNum",
  "SetSelectionStyleRef",
  "SetSelectionStylesRef",
  "SetSelectionStyleRefStr",
  "SetSelectionStyleRefColor",
  "SetSelectionStyleStr",
  "SetSelectionExportList",
  "SetSectionColorStyle",
  "SetSelectionGeometryNum",
  "SetOutlineWidth",
  "SetSidebarSize",
  "SetFollowing",
  "SetPointerType",
  "SetSliceOrientation",
  "RenameSection",
  "SetupState",
  "SetIsDraggingCommentPin",
  "ClearWorkspaceEditors",
  "ToggleSelectionListInfo",
  "InsertSvg",
  "InsertFrame",
  "OpenOuterObject",
  "OpenDocument",
  "CloseDocument",
  "SetFileEnvironmentContext",
  "OuterObjectPackageId",
  "InsertLibraryWidget",
  "InsertComponent",
  "InsertTemplate",
  "InsertAxvgMini",
  "GetInsertLocation",
  "AddFill",
  "SetSelectionFill",
  "SetSelectionFillItemVisibility",
  "RemoveFill",
  "SetSelectionFillItemType",
  "SetGradientEditMode",
  "RotateGradient",
  "AddStop",
  "UpdateGradientStop",
  "HighlightDiagramObjects",
  "SelectDiagramObjects",
  "RenameDiagramObject",
  "ToggleShowDiagramObjects",
  "ToggleLockDiagramObjects",
  "SetLinkToObject",
  "DragObjectsToTarget",
  "RemoveStop",
  "FlipGradient",
  "GetRecentFills",
  "SetFill",
  "AddRecentFill",
  "KillCombine",
  "FocusCanvasOrEditor",
]);

const getAvailableFonts = useBinaryReader(getAvailableFontsProxy);
const getProjectFonts = useBinaryReader(getProjectFontsProxy);
const getCurrentPage = useBinaryReader(getCurrentPageProxy);
const getDiagramObject = useBinaryReader(getDiagramObjectProxy);
const getDiagramObject2 = useBinaryReader(getDiagramObject2Proxy);
const getDiagramObjectAt = useBinaryReader(getDiagramObjectAtProxy);
const getDiagramObjects = useBinaryReader(getDiagramObjectsProxy);
const getDiagramObjects2 = useBinaryReader(getDiagramObjects2Proxy);
const getDiagramSections = useBinaryReader(getDiagramSectionsProxy);
function clearPreviewDiagramSelectionObject() {
  try {
    // execute clear preview safely
    return clearPreviewDiagramSelectionObjectProxy();
  } catch {
    // ignore
  }
}
const getLimitedSelectionContextMenu = useJsonReader(getLimitedSelectionContextMenuItemProxy);
const getSelectionContextMenuItem = useJsonReader(getSelectionContextMenuItemProxy);
const getLibraryBehaviorContextMenuItem = useJsonReader(getLibraryBehaviorContextMenuItemProxy);

const getRecentFills = useBinaryReader(getRecentFillsProxy);

export {
  addFill,
  addRecentFill,
  addStop,
  // setSelectionStyle,
  clearPreviewDiagramSelectionObject,
  clearWorkspaceEditors,
  closeDocument,
  cueCrop,
  dragObjectsToTarget,
  exitSliceImgMode,
  flipGradient,
  focusCanvasOrEditor,
  getActiveEditorModeName,
  getActiveGlobalCommandName,
  getAvailableFonts,
  getCurrentPage,
  getCurrentSelection,
  getDiagramObject,
  getDiagramObject2,
  getDiagramObjectAt,
  getDiagramObjects,
  getDiagramObjects2,
  getDiagramSections,
  getInsertLocation,
  getLibraryBehaviorContextMenuItem,
  getLimitedSelectionContextMenu,
  getProjectFonts,
  getRecentFills,
  getSelectionContextMenuItem,
  getSelectionImgThumbnail,
  getTrackedDiagramObject,
  highlightDiagramObjects,
  insertAxvgMini,
  insertComponent,
  insertFrame,
  insertLibraryWidget,
  insertSvg,
  insertTemplate,
  isDiagramEmpty,
  isPointIntoView,
  killCombine,
  openDocument,
  openOuterObject,
  outerObjectPackageId,
  previewDiagramSelectionObject,
  removeFill,
  removeStop,
  removeTrackedDiagramObject,
  renameDiagramObject,
  renameSection,
  rotateGradient,
  scrollToObject,
  scrollToPoint,
  selectDiagramObjects,
  setFileEnvironmentContext,
  setFill,
  setFitToContent,
  setFitToImage,
  setFollowing,
  setGradientEditMode,
  setImage,
  setIsDraggingCommentPin,
  setLinkToObject,
  setOutlineWidth,
  setPointerType,
  setSectionColorStyle,
  setSelectionConnectorFlowType,
  setSelectionExportList,
  setSelectionFill,
  setSelectionFillItemType,
  setSelectionFillItemVisibility,
  setSelectionGeometryNum,
  setSelectionShape,
  setSelectionStyleBool,
  setSelectionStyleNum,
  setSelectionStyleRef,
  setSelectionStyleRefColor,
  setSelectionStyleRefStr,
  setSelectionStylesNum,
  setSelectionStylesRef,
  setSelectionStyleStr,
  setSidebarSize,
  setSliceOrientation,
  setTrackedDiagramObject,
  setup,
  setupState,
  showSelectionContextMenu,
  toggleLockDiagramObjects,
  toggleSelectionListInfo,
  toggleShowDiagramObjects,
  updateGradientStop,
};
